import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { resetCartAsync } from '../features/cart/cartSlice';
import { useDispatch } from 'react-redux';
import { resetOrder, clearPendingOrder } from '../features/order/orderSlice';
import { useLanguage } from '../context/LanguageContext';
import { useCommonTranslations } from '../context/commonTranslations';
import {
  CheckCircleIcon,
  HomeIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../components/MainLayout';

function OrderSuccessPage() {
  const params = useParams();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const t = useCommonTranslations(language);

  useEffect(() => {
    // reset cart
    dispatch(resetCartAsync());
    // reset currentOrder
    dispatch(resetOrder());
    // clear pending order from receipt upload
    dispatch(clearPendingOrder());
  }, [dispatch]);

  return (
    <MainLayout>
      {!params.id && <Navigate to="/" replace={true}></Navigate>}
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Success Card */}
          <div className="bg-white rounded-xl shadow-subtle border border-slate-200 p-8 sm:p-10 text-center">
            {/* Success Icon */}
            <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <CheckCircleIcon className="w-12 h-12 text-emerald-500" />
            </div>

            {/* Success Message */}
            <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-2">
              {t.orderSuccess.pageTitle}
            </h1>
            <p className="text-sky-600 font-medium text-lg mb-4">
              {t.orderSuccess.orderNumber} #{params?.id}
            </p>
            <p className="text-slate-500 mb-8">
              {t.orderSuccess.thankYouMessage}
            </p>

            {/* Order Info */}
            <div className="bg-slate-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="text-sm font-medium text-slate-700 mb-4">
                {t.orderSuccess.whatsNext}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-sky-600">1</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    {t.orderSuccess.step1}
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-sky-600">2</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    {t.orderSuccess.step2}
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-sky-600">3</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    {t.orderSuccess.step3}
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-sky-600">4</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    {t.orderSuccess.step4}
                  </p>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 text-sm"
              >
                <HomeIcon className="w-4 h-4" />
                {t.orderSuccess.backToHome}
              </Link>
              <Link
                to="/my-orders"
                className="inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-sky-300 text-slate-700 hover:text-sky-600 font-medium py-3 px-6 rounded-xl transition-all duration-200 text-sm"
              >
                <ClipboardDocumentListIcon className="w-4 h-4" />
                {t.orderSuccess.viewMyOrders}
              </Link>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-400">
              {t.orderSuccess.supportNote}
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default OrderSuccessPage;
